import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsApi, configApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users,
  Receipt,
  CreditCard,
  AlertCircle,
  TrendingUp,
  Clock,
  Percent,
  Banknote,
  Database,
  Loader2,
  CheckCircle,
  Building2,
  CalendarRange,
  Wallet,
  Scale,
  RefreshCw,
  ArrowUpRight,
  FileMinus
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import NewAdmissionsSection from '../components/NewAdmissionsSection';

const formatCurrency = (amount, currency = 'USD') => {
  if (!amount) return `${currency} 0`;
  return `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// "Open" link in a section header - the quick-access route into that module.
const QuickLink = ({ onClick, label = 'Open' }) => (
  onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-[#3E3F96] hover:text-[#2D2E7A] hover:underline"
    >
      {label}
      <ArrowUpRight className="w-4 h-4" />
    </button>
  ) : null
);

const DashboardPage = () => {
  const { isAdmin, userBranches, hasPageAccess } = useAuth();
  const navigate = useNavigate();
  // Quick access: every dashboard section links to the module behind it, but
  // only when the user can open that page (otherwise it stays plain).
  const goTo = (path, pageKey) =>
    hasPageAccess(pageKey) ? () => navigate(path) : undefined;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [normStatus, setNormStatus] = useState(null);
  const [normalizing, setNormalizing] = useState(false);
  const [config, setConfig] = useState(null);
  // 'all' = aggregated across every branch the user can see; or a specific branch name.
  const [selectedBranch, setSelectedBranch] = useState('all');
  // 'all' = across all terms; or a specific term (Term 1/2/3).
  const [selectedTerm, setSelectedTerm] = useState('all');

  // Branches the toggle can offer = admin: all; non-admin: only their own.
  const availableBranches = isAdmin
    ? (config?.branches || [])
    : (userBranches || []);
  const showBranchToggle = availableBranches.length > 1;

  const checkNormalizationStatus = async () => {
    try {
      const res = await api.get('/admin/data-normalization-status');
      setNormStatus(res.data);
    } catch { /* ignore */ }
  };

  const handleNormalize = async () => {
    setNormalizing(true);
    try {
      const res = await api.post('/admin/normalize-student-data');
      toast.success(res.data.message);
      setNormStatus({ normalized: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Normalization failed');
    } finally {
      setNormalizing(false);
    }
  };

  // Live updates: the figures reload whenever any data behind them changes
  // (added, edited, approved, allocated or deleted - by anyone). A cheap
  // fingerprint (/reports/dashboard/version) is polled; the ~1s dashboard query
  // only re-runs when it moves.
  const POLL_MS = 30000;
  const versionRef = useRef(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Bumped on every background refresh so child sections reload too.
  const [refreshKey, setRefreshKey] = useState(0);

  // `silent` = background refresh: keep the page on screen (no full spinner).
  const fetchDashboardStats = useCallback(async (branch, term, silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const params = {};
      if (branch && branch !== 'all') params.branch = branch;
      if (term && term !== 'all') params.term = term;
      // Read the fingerprint first: a change landing mid-query then still
      // triggers the next refresh rather than being marked as seen.
      const ver = await reportsApi.dashboardVersion().then((r) => r.data.version).catch(() => null);
      const response = await reportsApi.dashboard(params);
      setStats(response.data);
      setError(null);
      if (ver) versionRef.current = ver;
      setLastUpdated(new Date());
    } catch (err) {
      if (!silent) setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, []);

  const refreshNow = useCallback(() => {
    setRefreshKey((k) => k + 1);
    return fetchDashboardStats(selectedBranch, selectedTerm, true);
  }, [fetchDashboardStats, selectedBranch, selectedTerm]);

  // Poll for changes while the page is visible; check at once when the user
  // comes back to the tab/window.
  useEffect(() => {
    let busy = false;
    const check = async () => {
      if (busy || document.visibilityState !== 'visible' || !versionRef.current) return;
      busy = true;
      try {
        const { data } = await reportsApi.dashboardVersion();
        if (data?.version && data.version !== versionRef.current) await refreshNow();
      } catch { /* offline / logged out - try again next tick */ }
      finally { busy = false; }
    };
    const timer = setInterval(check, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refreshNow]);

  // Load config (for branch list) + initial dashboard once on mount.
  useEffect(() => {
    configApi.get().then((r) => setConfig(r.data)).catch(() => {});
    if (isAdmin) checkNormalizationStatus();
  }, [isAdmin]);

  // Refetch dashboard whenever the branch or term toggle changes.
  useEffect(() => {
    fetchDashboardStats(selectedBranch, selectedTerm);
  }, [selectedBranch, selectedTerm, fetchDashboardStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3E3F96]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#C85A40]/10 border border-[#C85A40]/20 text-[#C85A40] px-4 py-3 rounded-lg flex items-center gap-2">
        <AlertCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  const withScope = (title) => (stats?.term_period ? `${title} (${stats.term_period})` : title);

  const lifecycle = stats?.student_lifecycle || {};

  // Total Invoiced — revenue-wise (by fee head, net of discount).
  const REVENUE_HEADS = [
    { label: 'Tuition', keys: ['tuition'] },
    { label: 'Lunch', keys: ['lunch'] },
    { label: 'Club', keys: ['club'] },
    { label: 'Text Book', keys: ['textbooks'] },
    { label: 'Uniform', keys: ['uniform'] },
    { label: 'Ctl Lvy', keys: ['capital_levy'] },
    { label: 'Exam Reg.', keys: ['examination', 'registration'] },
  ];
  const invByHead = stats?.invoiced_by_head || {};
  const mappedKeys = new Set(REVENUE_HEADS.flatMap((h) => h.keys));
  const revenueRows = REVENUE_HEADS.map((h) => ({
    label: h.label,
    keys: h.keys,
    amount: h.keys.reduce((s, k) => s + (invByHead[k] || 0), 0),
  }));
  const otherKeys = Object.keys(invByHead).filter((k) => !mappedKeys.has(k));
  const otherAmount = otherKeys.reduce((s, k) => s + (invByHead[k] || 0), 0);
  if (otherAmount > 0) revenueRows.push({ label: 'Other', keys: otherKeys, amount: otherAmount });
  const revenueTotal = revenueRows.reduce((s, r) => s + r.amount, 0);

  const cn = stats?.credit_notes || {};

  const statCards = [
    {
      title: 'Total Students',
      value: stats?.total_students || 0,
      icon: Users,
      color: 'bg-[#3E3F96]',
      textColor: 'text-[#3E3F96]',
      adminOnly: false,
      onClick: goTo('/students', 'students'),
      // Same split as the Students page tabs; each line opens its tab.
      subLines: [
        { label: 'Live Students', value: lifecycle.live ?? 0, onClick: goTo('/students?tab=live', 'students') },
        { label: 'Qualified Students', value: lifecycle.qualified ?? 0, onClick: goTo('/students?tab=qualified', 'students') },
        { label: 'Left Students', value: lifecycle.left ?? 0, onClick: goTo('/students?tab=left', 'students') },
      ]
    },
    {
      // The financial cards mirror Term Financials. The backend says what they
      // cover: one term ("2026-2027 Term 1") or, with no term, the current
      // academic year ("2026-2027").
      title: withScope('Opening Balance (owed)'),
      value: formatCurrency(stats?.opening_owed),
      icon: Wallet,
      color: 'bg-[#3E7F96]',
      textColor: 'text-[#3E7F96]',
      adminOnly: false,
      onClick: goTo('/reports?tab=term-financials', 'reports'),
      // Both sides: owed by students who owe (headline) and credit held for
      // students who paid ahead; net = owed + credit.
      subLines: [
        { label: 'Credit held (paid ahead)', value: formatCurrency(stats?.opening_credit) },
        { label: 'Net', value: formatCurrency(stats?.total_opening_balance) },
      ]
    },
    {
      title: withScope('Total Invoiced (Net of Discount)'),
      value: formatCurrency(stats?.total_invoiced),
      icon: Receipt,
      color: 'bg-[#5556A8]',
      textColor: 'text-[#5556A8]',
      adminOnly: true,
      onClick: goTo('/invoices', 'invoices')
    },
    {
      title: withScope('Total Collected'),
      value: formatCurrency(stats?.total_collected),
      icon: CreditCard,
      color: 'bg-[#D99C3B]',
      textColor: 'text-[#D99C3B]',
      adminOnly: false,
      onClick: goTo('/payments', 'payments'),
      subLines: [
        { label: 'Bank', value: formatCurrency(stats?.total_collected_bank) },
        { label: 'Cash', value: formatCurrency(stats?.total_collected_cash) },
        // Received in this scope but not yet allocated to an invoice. It IS in the
        // total above (money is booked by the date received) - shown so staff can
        // see how much still needs allocating.
        { label: 'Of which unallocated', value: formatCurrency(stats?.total_unallocated) },
      ]
    },
    {
      title: 'Outstanding (Overdue)',
      value: formatCurrency(stats?.total_outstanding),
      icon: Clock,
      color: 'bg-[#C85A40]',
      textColor: 'text-[#C85A40]',
      adminOnly: true,
      onClick: goTo('/reports?tab=outstanding', 'reports')
    },
    {
      title: withScope('Closing Outstanding (owed)'),
      value: formatCurrency(stats?.closing_owed),
      icon: Scale,
      color: 'bg-[#7A5AA8]',
      textColor: 'text-[#7A5AA8]',
      adminOnly: true,
      onClick: goTo('/reports?tab=term-financials', 'reports'),
      // Closing = Opening + Invoiced − Collected − Credit Notes; show the credit
      // notes so the card's arithmetic can be followed.
      subLines: [
        { label: 'Credit held (paid ahead)', value: formatCurrency(stats?.closing_credit) },
        { label: 'Net', value: formatCurrency(stats?.total_closing_outstanding) },
        { label: 'After credit notes', value: formatCurrency(stats?.total_credit_notes) },
      ],
    },
    {
      // Credit Notes card: every note raised in scope, split by status. Same
      // period rule as the Credit Notes list below (approved -> approval date,
      // pending/rejected -> raised date); Approved = Term Financials' figure.
      title: withScope('Credit Notes'),
      value: formatCurrency(cn.total),
      icon: FileMinus,
      color: 'bg-[#B5476B]',
      textColor: 'text-[#B5476B]',
      adminOnly: false,
      onClick: goTo('/credit-notes', 'credit_notes'),
      subLines: [
        { label: `Approved (${cn.approved_count || 0})`, value: formatCurrency(cn.approved_total), onClick: goTo('/credit-notes?status=approved', 'credit_notes') },
        { label: `Pending (${cn.pending_count || 0})`, value: formatCurrency(cn.pending_total), onClick: goTo('/credit-notes?status=pending', 'credit_notes') },
        { label: `Rejected (${cn.rejected_count || 0})`, value: formatCurrency(cn.rejected_total), onClick: goTo('/credit-notes?status=rejected', 'credit_notes') },
      ],
    },
    {
      // Total Invoiced — Revenue-wise card: the same fee-head split as the
      // Revenue-wise section below (net of discount); each line opens Invoices
      // filtered to that fee head.
      title: withScope('Total Invoiced — Revenue-wise'),
      value: formatCurrency(revenueTotal),
      icon: Receipt,
      color: 'bg-[#2E8B6E]',
      textColor: 'text-[#2E8B6E]',
      adminOnly: true,
      onClick: goTo('/invoices', 'invoices'),
      subLines: revenueRows.map((r) => ({
        label: r.label,
        value: formatCurrency(r.amount),
        onClick: r.keys.length ? goTo(`/invoices?fee_head=${r.keys.join(',')}`, 'invoices') : undefined,
      })),
    }
  ].filter(card => !card.adminOnly || isAdmin);

  const alertCards = [
    {
      title: 'Pending Discounts',
      value: stats?.pending_discounts || 0,
      icon: Percent,
      description: 'Awaiting approval',
      onClick: goTo('/discounts', 'discounts')
    },
    {
      title: 'Suspense Transactions',
      value: stats?.suspense_transactions || 0,
      icon: Banknote,
      description: 'Need reconciliation',
      onClick: goTo('/bank-reconciliation', 'bank_reconciliation')
    }
  ];

  // Prepare chart data for fee collection by head
  const collectionData = stats?.collection_by_head 
    ? Object.entries(stats.collection_by_head).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        amount: value
      }))
    : [];

  const COLORS = ['#3E3F96', '#5556A8', '#D99C3B', '#C85A40', '#5C635F', '#2D2E7A'];

  return (
    <div className="space-y-6">
      {/* First-time Data Loader Banner */}
      {isAdmin && normStatus && !normStatus.normalized && (
        <div className="bg-[#3E3F96]/5 border border-[#3E3F96]/20 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fade-in-up" data-testid="data-loader-banner">
          <div className="flex items-start gap-3">
            <div className="bg-[#3E3F96] p-2.5 rounded-lg mt-0.5">
              <Database className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1C1F1D]">First-Time Setup: Import 848 Students</h3>
              <p className="text-sm text-[#5C635F] mt-1">
                Import all 848 students from the preloaded data file and normalize branch/class names 
                to match the master configuration (e.g., "Kisaasi" becomes "KISAASI"). This runs once.
              </p>
            </div>
          </div>
          <Button
            onClick={handleNormalize}
            disabled={normalizing}
            className="bg-[#3E3F96] hover:bg-[#2D2E7A] whitespace-nowrap"
            data-testid="normalize-data-btn"
          >
            {normalizing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>
            ) : (
              <><Database className="w-4 h-4 mr-2" />Import Student Data</>
            )}
          </Button>
        </div>
      )}

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 animate-fade-in-up">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Dashboard</h1>
          <p className="text-[#5C635F] mt-1">
            Welcome back! Here's an overview of
            {' '}
            <span className="font-medium text-[#1C1F1D]">
              {selectedBranch === 'all' ? 'all branches' : `${selectedBranch} branch`}
            </span>
            's finances.
          </p>
          <p className="text-xs text-[#5C635F] mt-1 flex items-center gap-2" data-testid="dashboard-last-updated">
            <span className="inline-flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${refreshing ? 'bg-[#D99C3B] animate-pulse' : 'bg-green-500'}`} />
              {refreshing ? 'Updating…' : `Live · updated ${lastUpdated ? lastUpdated.toLocaleTimeString() : '—'}`}
            </span>
            <button
              type="button"
              onClick={refreshNow}
              disabled={refreshing}
              className="inline-flex items-center gap-1 text-[#3E3F96] hover:underline disabled:opacity-50"
              data-testid="dashboard-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />Refresh
            </button>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {showBranchToggle && (
            <div className="flex items-center gap-2" data-testid="dashboard-branch-toggle">
              <Building2 className="w-4 h-4 text-[#5C635F]" />
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-full sm:w-48" data-testid="branch-toggle-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {availableBranches.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center gap-2" data-testid="dashboard-term-toggle">
            <CalendarRange className="w-4 h-4 text-[#5C635F]" />
            <Select value={selectedTerm} onValueChange={setSelectedTerm}>
              <SelectTrigger className="w-full sm:w-40" data-testid="term-toggle-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Terms</SelectItem>
                {(config?.terms || ['Term 1', 'Term 2', 'Term 3']).map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        {statCards.map((stat, index) => (
          <Card
            key={stat.title}
            className={`border-[#D8D9E8] card-hover${stat.onClick ? ' cursor-pointer hover:border-[#3E3F96]/50 hover:shadow-md transition' : ''}`}
            onClick={stat.onClick}
            data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#5C635F]">{stat.title}</p>
                  <p className={`text-2xl font-bold ${stat.textColor} tabular-nums mt-1`}>
                    {stat.value}
                  </p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
              {stat.subLines && (
                <div className="mt-3 pt-3 border-t border-[#ECEDF5] space-y-1">
                  {stat.subLines.map((sl) => (
                    <div
                      key={sl.label}
                      onClick={sl.onClick ? (e) => { e.stopPropagation(); sl.onClick(); } : undefined}
                      className={`flex items-center justify-between text-sm${sl.onClick ? ' rounded px-1 -mx-1 hover:bg-[#F5F5FA] hover:text-[#3E3F96]' : ''}`}
                      data-testid={sl.onClick ? `subline-${sl.label.toLowerCase().replace(/\s*\(\d+\)/, '').replace(/\s+/g, '-')}` : undefined}
                    >
                      <span className="text-[#5C635F]">{sl.label}</span>
                      <span className="font-medium text-[#1C1F1D] tabular-nums">{sl.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* New Admissions — term-wise intake per branch. Follows the branch toggle
          above; it has its own academic-year filter (the term toggle above is a
          financial filter and doesn't apply to headcount). */}
      <NewAdmissionsSection
        branch={selectedBranch}
        refreshKey={refreshKey}
        onOpenReport={goTo('/reports?tab=new-admission', 'reports')}
      />

      {/* Alerts row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {alertCards.map((alert) => (
          <Card
            key={alert.title}
            className={`border-[#D8D9E8] border-l-4 border-l-[#D99C3B]${alert.onClick ? ' cursor-pointer hover:border-[#3E3F96]/50 hover:shadow-md transition' : ''}`}
            onClick={alert.onClick}
            data-testid={`alert-${alert.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="bg-[#D99C3B]/10 p-2 rounded-lg">
                <alert.icon className="w-5 h-5 text-[#D99C3B]" />
              </div>
              <div>
                <p className="font-medium text-[#1C1F1D]">{alert.title}</p>
                <p className="text-sm text-[#5C635F]">{alert.value} {alert.description}</p>
              </div>
              {alert.onClick && <ArrowUpRight className="w-4 h-4 ml-auto text-[#3E3F96]" />}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Collection by Fee Head */}
        <Card className="border-[#D8D9E8]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1C1F1D] flex items-center gap-2">
              Collection by Fee Head
              <QuickLink onClick={goTo('/payments', 'payments')} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {collectionData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={collectionData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#D8D9E8" />
                    <XAxis type="number" tickFormatter={(value) => `${(value / 1000000).toFixed(0)}M`} />
                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #D8D9E8' }}
                    />
                    <Bar dataKey="amount" fill="#3E3F96" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-[#5C635F]">
                No collection data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card className="border-[#D8D9E8]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1C1F1D] flex items-center gap-2">
              Recent Payments
              <QuickLink onClick={goTo('/payments', 'payments')} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_payments && stats.recent_payments.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_payments.map((payment, index) => (
                  <div 
                    key={payment.id || index}
                    className="flex items-center justify-between p-3 bg-[#F5F5FA] rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-[#1C1F1D] text-sm">{payment.student_name}</p>
                      <p className="text-xs text-[#5C635F]">{payment.receipt_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#5556A8] tabular-nums">
                        {formatCurrency(payment.amount_usd || payment.amount, 'USD')}
                      </p>
                      <p className="text-xs text-[#5C635F] capitalize">{payment.payment_mode?.replace('_', ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-[#5C635F]">
                No recent payments
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Collection Distribution Pie Chart */}
      {collectionData.length > 0 && (
        <Card className="border-[#D8D9E8]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1C1F1D] flex items-center gap-2">
              Collection Distribution
              <QuickLink onClick={goTo('/reports?tab=collection', 'reports')} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={collectionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="amount"
                  >
                    {collectionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #D8D9E8' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
